import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import FileUploadMetaFields from "../../components/FileUploadMetaFields";
import { todayIsoDate } from "../../utils/fileMetaDisplay";

const PlanUpload = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState("");
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [file, setFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [issueDate, setIssueDate] = useState(() => todayIsoDate());
  const [author, setAuthor] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState(null);

  const project = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const projectId = project ? project.id : null;

  useEffect(() => {
    const role = localStorage.getItem("userRole");
    if (!role || !projectId) {
      navigate("/");
    } else {
      setUserRole(role);
      fetchFolders();
    }
  }, [navigate, projectId]);

  const fetchFolders = async () => {
    try {
      const res = await fetch(`/api/folders?module=plan&projectId=${projectId}&hierarchical=true`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setFolders(data);
      } else if (data.folders && Array.isArray(data.folders)) {
        setFolders(data.folders);
      } else {
        console.error("Unexpected folders data structure:", data);
        setFolders([]);
      }
    } catch (err) {
      console.error("Error fetching folders:", err);
      setFolders([]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage("");
    setError(null);

    if (!file || !selectedFolderId || !displayName.trim() || !issueDate) {
      setError("Please select a folder and file, and enter display name and issue date.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderId", selectedFolderId);
    formData.append("projectId", projectId);
    formData.append("display_name", displayName.trim());
    formData.append("issue_date", issueDate);
    formData.append("author", author);
    formData.append("user_id", localStorage.getItem("userId"));

    try {
      const response = await fetch("/api/upload/plan", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error("Upload failed");
      await response.json();
      setMessage("File uploaded successfully!");
      setFile(null);
      setDisplayName("");
      setIssueDate(todayIsoDate());
      setAuthor("");
      setSelectedFolderId("");
    } catch (err) {
      setError(err.message);
    }
  };

  const renderFolderOption = (folder) => {
    const indent = "  ".repeat(folder.level - 1);
    const levelIndicator = `[L${folder.level}]`;
    return (
      <option key={folder._id} value={folder._id}>
        {indent}{levelIndicator} {folder.name}
      </option>
    );
  };

  return (
    <div className="flex h-screen">
      <Sidebar userRole={userRole} projectId={projectId} scenario="plan" />

      <div className="flex-1 p-6 bg-gray-50 text-[12px] ml-[60px]">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-[18px]">Upload Plan File</h2>
          <img src="/images/gemterra-logo-20260112-t.png" alt="Gemterra" className="h-6" />
        </div>

        <button
          onClick={() => navigate("/planfile_page")}
          className="bg-gray-300 text-black px-3 py-1 rounded hover:bg-gray-400 mb-4 text-[12px]"
        >
          Back
        </button>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {message && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {message}
          </div>
        )}

        <form onSubmit={handleUpload} className="bg-white p-6 rounded shadow max-w-[480px]">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Folder (showing hierarchical structure)
            </label>
            <select
              value={selectedFolderId}
              onChange={(e) => setSelectedFolderId(e.target.value)}
              className="w-full p-2 border rounded h-32"
              size="6"
              required
            >
              <option value="">Select a folder</option>
              {folders.map((folder) => renderFolderOption(folder))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full p-2 border rounded text-[12px]"
              required
            />
          </div>

          <div className="mb-6 space-y-4">
            <FileUploadMetaFields
              displayName={displayName}
              onDisplayNameChange={setDisplayName}
              issueDate={issueDate}
              onIssueDateChange={setIssueDate}
              author={author}
              onAuthorChange={setAuthor}
              ids={{
                display: "plan-page-upload-display",
                issue: "plan-page-upload-issue",
                author: "plan-page-upload-author",
              }}
            />
          </div>

          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 text-[12px]"
          >
            Upload File
          </button>
        </form>
      </div>
    </div>
  );
};

export default PlanUpload;